import {APP_INITIALIZER, ApplicationConfig, enableProdMode, importProvidersFrom, ValueProvider} from '@angular/core';
import {provideRouter, withInMemoryScrolling} from '@angular/router';

import {routes, ROUTES_TRANSLATIONS, SEO_PAGES} from './app.routes';
import {TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core';
import {HttpClient, HttpClientModule} from '@angular/common/http';

import {
  FILTERS_PROVIDER,
  IMAGES_DATA_PROVIDER,
  ImagesService,
  ISeoConfig,
  MAIL_API_URL,
  MailService,
  provideSeo,
  RentsService,
  APP_FACEBOOK_URL,
  APP_INSTAGRAM_URL,
  APP_SHOW_SOCIAL_ICON,
  APP_TITLE,
  APP_DEFAULT_LANG,
  APP_LOGO_WHITE_URL,
  APP_ROUTES_TRANSLATIONS,
  CONTACT_ASSET,
  FAQ_DATA_PROVIDER,
  HOME_DATA_PROVIDER,
  AVAILABILITIES_SERVICE,
  RENT_DETAILS_CONFIG,
  APP_SEO_PAGES,
  APP_DEPLOY_URL, APP_RENTALS, APP_CDN_URL, APP_TWICPICS_DOMAIN, twicpicsInitFactory, APP_LOGO
} from '@olivades/pattern-lib';
import {environment} from '../environments/environment';
import {AllFilters} from "./data/filters/filter";
import {contactPhoto, logo, photoTab} from "./data/photo";
import {aboutData, accessibilitiesData, amenitiesData, introData} from "./data/home";
import {faqData} from "./data/faq";
import {forkJoin, Observable, of} from 'rxjs';
import { map } from 'rxjs/operators';
import {LodgifyAvalabilitiesService} from "@olivades/lodgify";
import {AllLocations} from "./data/locations";

if (environment.production) {
  enableProdMode();
}

export function appInitializerFactory(translate: TranslateService) {
  return () => translate.use('fr').toPromise();
}

const seoConfig: ISeoConfig = { siteName: environment.siteName, siteUrl: environment.siteUrl };

export class MultiTranslateHttpLoader implements TranslateLoader {
  constructor(
    private http: HttpClient,
    private urls: string[],
    private suffix: string = '.json'
  ) {}

  public getTranslation(lang: string): Observable<any> {
    const requests = this.urls.map(url => this.http.get(`${url}${lang}${this.suffix}`));
    return forkJoin(requests).pipe(
      map(responses => {
        return Object.assign({}, ...responses);
      })
    );
  }
}

export function multiLoaderFactory(http: HttpClient) {
  return new MultiTranslateHttpLoader(http, [
    `${environment.deployUrl}/assets/i18n/`,
    `${environment.deployUrl}/assets/pattern-lib/i18n/`
  ]);
}

export const appConfig: ApplicationConfig = {
  providers: [
    // 1️⃣ PROVIDERS Angular sous forme d’EnvironmentProviders
    importProvidersFrom(
      HttpClientModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: multiLoaderFactory,
          deps: [HttpClient]
        }
      })
    ),
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService],
      multi: true
    },
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled'
      })
    ),
    provideSeo(seoConfig),

    // 2️⃣ PROVIDERS normaux
    { provide: 'env', useValue: environment },

    { provide: APP_LOGO, useValue: logo },
    { provide: APP_LOGO_WHITE_URL, useValue: "https://cdn.residencemelody.com/Logo/BLANC/Logo_Blanc.png" },
    { provide: APP_FACEBOOK_URL, useValue: "https://www.facebook.com/residencemelodysaintremy" },
    { provide: APP_INSTAGRAM_URL, useValue: "https://www.instagram.com/residence_melody/" },
    { provide: APP_SHOW_SOCIAL_ICON, useValue: true },
    { provide: APP_TITLE, useValue: environment.siteName },
    { provide: APP_DEFAULT_LANG, useValue: 'fr' },
    { provide: APP_ROUTES_TRANSLATIONS, useValue: ROUTES_TRANSLATIONS },
    { provide: APP_SEO_PAGES, useValue: SEO_PAGES },

    { provide: FILTERS_PROVIDER, useValue: AllFilters },
    { provide: RentsService },

    { provide: MAIL_API_URL, useValue: environment.apiGatewayUrl + 'email' },
    { provide: MailService },

    { provide: IMAGES_DATA_PROVIDER, useValue: photoTab },
    { provide: ImagesService },
    { provide: APP_DEPLOY_URL, useValue: environment.deployUrl } as ValueProvider,

    { provide: CONTACT_ASSET, useValue: contactPhoto },

    { provide: FAQ_DATA_PROVIDER, useValue: faqData },

    {
      provide: HOME_DATA_PROVIDER,
      useValue: {
        intro: introData,
        about: aboutData,
        amenities: amenitiesData,
        accessibilities: accessibilitiesData
      }
    },

    { provide: RENT_DETAILS_CONFIG, useValue: { defaultAdults: 2, defaultChildrens: 0, minChildrenAge: 18 } },
    { provide: AVAILABILITIES_SERVICE, useClass: LodgifyAvalabilitiesService },
    { provide: APP_RENTALS, useValue: AllLocations },
    { provide: APP_CDN_URL, useValue: environment.cdnUrl },
    { provide: APP_TWICPICS_DOMAIN, useValue: environment.twicPicsUrl },
    {
      provide: APP_INITIALIZER,
      useFactory: twicpicsInitFactory,
      deps: [APP_TWICPICS_DOMAIN],   // ⬅ utilise deps ici
      multi: true
    }
  ]
};

